import { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import API_URLS from "../../../../constants/apiConstants";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import styles from "../../css/activityReport.module.css";
import { DatePicker } from "../../../../components/components/js/DatePicker";
import { GeneralDropdown } from "../../../../components/components/js/GeneralDropdown";
import { LoadingDots } from "../../../../components/general";

// V2 note: the old single `/en/api/nursery/individual/` call is now split
// into 5 endpoints (see apiConstants.js). Two backend-shape changes that
// affect this screen:
//   1. report/summary/ no longer returns score/average fields — V1's
//      memorization_avg / revision_avg have no V2 equivalent yet, so the
//      old "Performance Summary" averages card is dropped here. Follow up
//      with backend if those averages are still wanted.
//   2. "recitation_records" (one merged list) is now 4 separate paginated
//      lists: homework/memorization, homework/revision,
//      recitation/memorization, recitation/revision. Homework records only
//      have a note (no score/mistakes); recitation records add score +
//      mistakes_telawa + mistakes_tagweed. They're rendered as two
//      separate tables below instead of one merged table.
// Pagination: each report endpoint is independently paginated. To keep the
// single "Filter" button UX from V1, this fetches page 1 at a generous
// page_size instead of building per-table pagination controls. Revisit if
// nurseries start generating more records per range than PAGE_SIZE.
const PAGE_SIZE = 50;

const buildSuraDisplay = (record, lang, { revision = false } = {}) => {
  const mulNames = lang === "ar" ? record.sura_mul_ar : record.sura_mul_en?.length ? record.sura_mul_en : record.sura_mul;
  if (mulNames?.length) return mulNames.join(", ");
  return (lang === "ar" ? record.sura_ar : record.sura_en || record.sura) || "---";
};

const buildAyahRange = (record) => {
  if (record.aya_from == null || record.aya_to == null) return "";
  return record.aya_from === record.aya_to
    ? `${record.aya_from}`
    : `${record.aya_from}-${record.aya_to}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "---";
  return new Date(isoDate).toISOString().slice(0, 10);
};

export const IndividualReport = ({ reportRef }) => {
  const { t, i18n } = useTranslation();
  const cardDataPrefix = "admin_pages.quran_report.card_data";
  const cardTitlesPrefix = "admin_pages.quran_report.card_titles";
  const tableColsPrefix = "admin_pages.quran_report.recitation_table_cols";
  const filtersPrefix = "admin_pages.quran_report.filters";

  const trans = {
    // Card titles
    personalInfo: t(`${cardTitlesPrefix}.personal_info`),
    memorizationAchievements: t(`${cardTitlesPrefix}.memorization_achievements`),
    revisionAchievements: t(`${cardTitlesPrefix}.revision_achievements`, {
      defaultValue: "Revision Achievements",
    }),
    homeworkRecords: t(`${cardTitlesPrefix}.homework_records`, { defaultValue: "Homework Records" }),
    recitationRecords: t(`${cardTitlesPrefix}.recitation_records`),

    // Card data
    studentName: t(`${cardDataPrefix}.student_name`),
    room: t(`${cardDataPrefix}.room`),
    dateInterval: t(`${cardDataPrefix}.date_interval`),
    currentSura: t(`${cardDataPrefix}.current_sura`),
    currentRevision: t(`${cardDataPrefix}.current_revision`, { defaultValue: "Current Revision" }),
    totalSuwar: t(`${cardDataPrefix}.total_suwar`),
    totalAyat: t(`${cardDataPrefix}.total_ayat`),

    // Filters
    selectRoom: t(`${filtersPrefix}.select_room`),
    selectStudent: t(`${filtersPrefix}.select_student`),
    selectRoomFirst: t(`${filtersPrefix}.select_room_first`),
    noStudents: t(`${filtersPrefix}.no_students`),
    startDate: t(`${filtersPrefix}.start_date`),
    endDate: t(`${filtersPrefix}.end_date`),
    requiredMsg: t(`${filtersPrefix}.select_this_field`),
    filterBtn: t("buttons.filter"),
    filterLoadingBtn: t("buttons.filtering"),

    // Table columns
    date: t(`${tableColsPrefix}.date`),
    homework: t(`${tableColsPrefix}.homework`),
    sura: t(`${tableColsPrefix}.sura`),
    grade: t(`${tableColsPrefix}.grade`),
    mistakes: t(`${tableColsPrefix}.mistakes`),
    notes: t(`${tableColsPrefix}.notes`),
    noRecords: t(`${cardTitlesPrefix}.no_records`),
  };

  const homeworkColumns = [
    { key: "date", label: trans.date, className: styles.narrowCol },
    { key: "homework", label: trans.homework, className: styles.narrowCol },
    { key: "surah", label: trans.sura, className: styles.narrowCol },
    { key: "notes", label: trans.notes, className: styles.wideCol },
  ];

  const recitationColumns = [
    { key: "date", label: trans.date, className: styles.narrowCol },
    { key: "homework", label: trans.homework, className: styles.narrowCol },
    { key: "surah", label: trans.sura, className: styles.narrowCol },
    { key: "grade", label: trans.grade, className: styles.narrowCol },
    { key: "mistakes", label: trans.mistakes, className: styles.narrowCol },
    { key: "notes", label: trans.notes, className: styles.wideCol },
  ];

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [studentSummary, setStudentSummary] = useState({});
  const [homeworkTableData, setHomeworkTableData] = useState([]);
  const [recitationTableData, setRecitationTableData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);

  const { school } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoadingRooms(true);
        const api_url = API_URLS.getSchoolRooms(school.id);
        const response = await axiosPrivate.get(api_url);
        // Classrooms endpoint is paginated ({ results: [...] }), confirmed
        // via the same "data.map is not a function" bug hit on the
        // Student Attendance page — read `.results`, not the raw response.
        const results = response?.data?.results || [];
        const data = results.map((room) => ({
          value: room.id,
          label: room.name,
        }));

        setRooms(data);
      } catch (error) {
        // Do nothing
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, [axiosPrivate, school.id]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!selectedRoom) return;

      try {
        setLoadingStudents(true);
        // getRoomStudents (V1) no longer exists on the V2 backend.
        // getStudentsByRoom defaults pageSize to 1 (used elsewhere as a
        // cheap "just get the count" trick) so we pass an explicit larger
        // pageSize here to actually get the room's student list.
        const api_url = API_URLS.getStudentsByRoom(school.id, selectedRoom.value, 100);
        const response = await axiosPrivate.get(api_url);
        const results = response?.data?.results || [];
        const data = results.map((student) => ({
          value: student.id,
          label: `${student.first_name || ""} ${student.last_name || ""}`.trim(),
        }));

        setStudents(data);
      } catch (error) {
        // Do nothing
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudents();
  }, [axiosPrivate, school.id, selectedRoom]);

  // Clear students data on Room change
  useEffect(() => {
    setStudents([]);
    setSelectedStudent(null);
  }, [selectedRoom]);

  const mapRecords = (results, { revision = false, hasScore = false } = {}) =>
    (results || []).map((record) => ({
      date: formatDate(record.activity_date),
      homework: revision ? "Revision" : "Memorization",
      surah: [buildSuraDisplay(record, i18n.language, { revision }), buildAyahRange(record)]
        .filter(Boolean)
        .join(": "),
      ...(hasScore
        ? {
            grade: record.score ?? "---",
            mistakes: (record.mistakes_telawa || 0) + (record.mistakes_tagweed || 0),
          }
        : {}),
      notes: record.note || "---",
      // sort key
      _date: record.activity_date,
    }));

  const handleFilter = async () => {
    const newErrors = {
      room: !selectedRoom,
      student: !selectedStudent,
      startDate: !selectedStartDate,
      endDate: !selectedEndDate,
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some(Boolean);
    if (hasError) return;

    try {
      setLoadingResult(true);

      const studentId = selectedStudent.value;

      const [summaryRes, hwMemRes, hwRevRes, recMemRes, recRevRes] = await Promise.all([
        axiosPrivate.get(
          API_URLS.getQuranReportSummary(school.id, studentId, selectedStartDate, selectedEndDate)
        ),
        axiosPrivate.get(
          API_URLS.getQuranHomeworkMemorization(
            school.id,
            studentId,
            selectedStartDate,
            selectedEndDate,
            1,
            PAGE_SIZE
          )
        ),
        axiosPrivate.get(
          API_URLS.getQuranHomeworkRevision(
            school.id,
            studentId,
            selectedStartDate,
            selectedEndDate,
            1,
            PAGE_SIZE
          )
        ),
        axiosPrivate.get(
          API_URLS.getQuranRecitationMemorization(
            school.id,
            studentId,
            selectedStartDate,
            selectedEndDate,
            1,
            PAGE_SIZE
          )
        ),
        axiosPrivate.get(
          API_URLS.getQuranRecitationRevision(
            school.id,
            studentId,
            selectedStartDate,
            selectedEndDate,
            1,
            PAGE_SIZE
          )
        ),
      ]);

      const summary = summaryRes.data || {};
      setStudentSummary({
        currentMemorization: {
          en: summary.current_memorization,
          ar: summary.current_memorization_ar,
        },
        currentRevision: {
          en: summary.current_revision,
          ar: summary.current_revision_ar,
        },
        totalMemorizationSuwar: summary.memorization_sura,
        totalMemorizationAyat: summary.memorization_ayat,
        totalRevisionSuwar: summary.revision_sura,
        totalRevisionAyat: summary.revision_ayat,
      });

      const homework = [
        ...mapRecords(hwMemRes.data?.results, { revision: false, hasScore: false }),
        ...mapRecords(hwRevRes.data?.results, { revision: true, hasScore: false }),
      ].sort((a, b) => new Date(a._date) - new Date(b._date));

      const recitation = [
        ...mapRecords(recMemRes.data?.results, { revision: false, hasScore: true }),
        ...mapRecords(recRevRes.data?.results, { revision: true, hasScore: true }),
      ].sort((a, b) => new Date(a._date) - new Date(b._date));

      setHomeworkTableData(homework);
      setRecitationTableData(recitation);
    } catch (err) {
      // Do nothing
    } finally {
      setLoadingResult(false);
    }
  };

  const getCurrentSura = () => studentSummary?.currentMemorization?.[i18n.language] || "";
  const getCurrentRevision = () => studentSummary?.currentRevision?.[i18n.language] || "";

  const renderTable = (title, columns, data) => (
    <Row>
      <Col>
        <div className={`${styles.scrollX} ${styles.card}`}>
          <p className={styles.title}>{title}</p>
          <table className={`text-color text-center ${styles.table}`}>
            <thead style={{ borderBottom: "1px solid #E8E8E8" }}>
              <tr>
                {columns.map((col, index) => (
                  <th className="p-2 fw-bold fs-6" key={index}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!data.length && (
                <tr>
                  <td className="py-4 w-100 text-center" colSpan={columns.length} style={{ color: "#666" }}>
                    {trans.noRecords}
                  </td>
                </tr>
              )}
              {data?.map((row, index) => (
                <tr key={index}>
                  {columns.map((col, i) => (
                    <td className={`p-2 fw-semibold text-12 ${col.className}`} key={i}>
                      <span
                        className={
                          row[col.key] === "Memorization"
                            ? styles.memorization
                            : row[col.key] === "Revision"
                            ? styles.revision
                            : null
                        }
                      >
                        {row[col.key] === "Memorization" || row[col.key] === "Revision"
                          ? t(`${tableColsPrefix}.${row[col.key].toLowerCase()}`)
                          : row[col.key]}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Col>
    </Row>
  );

  return (
    <div className={styles.container}>
      <div className={styles.filtersContainer}>
        <GeneralDropdown
          placeholder={trans.selectRoom}
          options={rooms}
          selectedOption={selectedRoom}
          onChange={(option) => setSelectedRoom(option)}
          errMsg={errors.room && trans.requiredMsg}
          isClearable
          isSearchable
          disabled={loadingRooms}
        />

        <GeneralDropdown
          placeholder={trans.selectStudent}
          options={students}
          selectedOption={selectedStudent}
          onChange={(option) => setSelectedStudent(option)}
          noOptionsMessage={!selectedRoom ? trans.selectRoomFirst : trans.noStudents}
          errMsg={errors.student && trans.requiredMsg}
          isClearable
          isSearchable
          disabled={loadingStudents}
        />

        <DatePicker
          label={trans.startDate}
          date={selectedStartDate}
          setDate={setSelectedStartDate}
          errMsg={errors.startDate && trans.requiredMsg}
        />
        <DatePicker
          label={trans.endDate}
          date={selectedEndDate}
          setDate={setSelectedEndDate}
          errMsg={errors.endDate && trans.requiredMsg}
          minDate={selectedStartDate}
        />
        <Button onClick={handleFilter} disabled={loadingResult}>
          {loadingResult ? trans.filterLoadingBtn : trans.filterBtn}
        </Button>
      </div>

      {loadingResult ? (
        <LoadingDots className={styles.loadingContainer} />
      ) : (
        <div ref={reportRef}>
          <Row>
            <Col>
              <div className={`${styles.card} no-page-break`}>
                <p className={styles.title}>{trans.personalInfo}</p>
                <Row>
                  <Col xs={12} md={12} lg={6}>
                    <div className={styles.body}>
                      <div className={styles.personalInfo}>
                        <span>{trans.studentName}: </span>
                        <span>{selectedStudent?.label || "---"}</span>
                      </div>
                      <div className={styles.personalInfo}>
                        <span>{trans.room}: </span>
                        <span>{selectedRoom?.label || "---"}</span>
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} md={12} lg={6}>
                    <div className={styles.body}>
                      <div className={styles.personalInfo}>
                        <span>{trans.dateInterval}: </span>
                        <span>
                          {selectedStartDate && selectedEndDate
                            ? `${selectedStartDate} --> ${selectedEndDate}`
                            : "---"}
                        </span>
                      </div>
                      <div className={styles.personalInfo}>
                        <span>{trans.currentSura}: </span>
                        <span>{getCurrentSura() || "---"}</span>
                      </div>
                      <div className={styles.personalInfo}>
                        <span>{trans.currentRevision}: </span>
                        <span>{getCurrentRevision() || "---"}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={12} lg={6}>
              <div className={`${styles.card} no-page-break`}>
                <p className={styles.title}>{trans.memorizationAchievements}</p>
                <div className={styles.body}>
                  <div className={styles.info}>
                    <span>{trans.totalSuwar}</span>
                    <span>
                      {studentSummary.totalMemorizationSuwar == null
                        ? "---"
                        : studentSummary.totalMemorizationSuwar}
                    </span>
                  </div>
                  <div className={styles.info}>
                    <span>{trans.totalAyat}</span>
                    <span>
                      {studentSummary.totalMemorizationAyat == null
                        ? "---"
                        : studentSummary.totalMemorizationAyat}
                    </span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={12} lg={6}>
              <div className={`${styles.card} no-page-break`}>
                <p className={styles.title}>{trans.revisionAchievements}</p>
                <div className={styles.body}>
                  <div className={styles.info}>
                    <span>{trans.totalSuwar}</span>
                    <span>
                      {studentSummary.totalRevisionSuwar == null ? "---" : studentSummary.totalRevisionSuwar}
                    </span>
                  </div>
                  <div className={styles.info}>
                    <span>{trans.totalAyat}</span>
                    <span>
                      {studentSummary.totalRevisionAyat == null ? "---" : studentSummary.totalRevisionAyat}
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {renderTable(trans.homeworkRecords, homeworkColumns, homeworkTableData)}
          {renderTable(trans.recitationRecords, recitationColumns, recitationTableData)}
        </div>
      )}
    </div>
  );
};